
import { randomId } from "@/hooks/randomId";
import { useLocale } from "@/i18n";
import { useEffect, useRef } from "react";

const API_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:8000").replace(
  /\/+$/,
  "",
);
const EVENTS_URL = `${API_URL}/api/v1/public/deck-events`;

// Endpoint de tracking de share links (Etapa 6). Solo se usa cuando la URL trae ?share=<token>.
const SHARE_EVENTS_URL = `${(import.meta.env.VITE_DECK_API_URL ?? "").replace(
  /\/+$/,
  "",
)}/api/public/share-events`;

interface DeckEventPayload {
  deck_id: string;
  slide_id: string;
  slide_index: number;
  locale: string;
  session_id: string;
  event_type: "view" | "exit";
  ms_on_slide?: number;
}

// Envío best-effort: sendBeacon primero (sobrevive al unload), fetch keepalive como fallback.
function post(url: string, body: string) {
  const blob = new Blob([body], { type: "application/json" });
  if (navigator.sendBeacon?.(url, blob)) {
    return;
  }
  void fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

// Token del share link actual (si la URL fue abierta vía /d/:token -> ?share=<token>).
function shareToken(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("share");
}

function send(payload: DeckEventPayload) {
  const body = JSON.stringify(payload);
  post(EVENTS_URL, body);

  // Dual-send: si hay un share token, replica el evento al backend de decks para stats por link.
  const token = shareToken();
  if (token) {
    post(
      SHARE_EVENTS_URL,
      JSON.stringify({
        token,
        session_id: payload.session_id,
        slide_id: payload.slide_id,
        slide_index: payload.slide_index,
        event_type: payload.event_type,
        ...(payload.ms_on_slide !== undefined
          ? { ms_on_slide: payload.ms_on_slide }
          : {}),
      }),
    );
  }
}

export function useDeckAnalytics({
  deckId,
  slideId,
  slideIndex,
}: {
  deckId: string;
  slideId: string;
  slideIndex: number;
}) {
  const locale = useLocale();
  const sessionRef = useRef<string | null>(null);
  const currentRef = useRef<{ slideId: string; slideIndex: number; since: number } | null>(null);

  useEffect(() => {
    sessionRef.current ??= randomId();
    const sessionId = sessionRef.current;
    currentRef.current = { slideId, slideIndex, since: Date.now() };
    send({
      deck_id: deckId,
      slide_id: slideId,
      slide_index: slideIndex,
      locale,
      session_id: sessionId,
      event_type: "view",
    });
  }, [deckId, slideId, slideIndex, locale]);

  useEffect(() => {
    function flushExit() {
      const sessionId = sessionRef.current;
      const current = currentRef.current;
      if (!sessionId || !current || document.visibilityState !== "hidden") return;
      send({
        deck_id: deckId,
        slide_id: current.slideId,
        slide_index: current.slideIndex,
        locale,
        session_id: sessionId,
        event_type: "exit",
        ms_on_slide: Date.now() - current.since,
      });
    }
    document.addEventListener("visibilitychange", flushExit);
    return () => document.removeEventListener("visibilitychange", flushExit);
  }, [deckId, locale]);
}
